import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatPnL, getPnLColor } from '../../utils/formatters';
import { getOrders } from '../../api/orders';
import { STATUS_META } from '../../constants';
import styles from './Dashboard.module.css';

import dhanLogo    from '../../assets/brokers/dhan.png';
import zerodhaLogo from '../../assets/brokers/zerodha.png';

/* ══════════════════════════════════════════════
   BROKER CONFIG
══════════════════════════════════════════════ */
const BROKER_CONFIG = {
  dhan: { dot: '#00b386', bg: '#ffffff',               logo: dhanLogo,    letter: 'D', color: '#00b386' },
  kite: { dot: '#387ed1', bg: '#ffffff',               logo: zerodhaLogo, letter: 'Z', color: '#387ed1' },
};

function getBrokerConfig(name = '') {
  return BROKER_CONFIG[name.toLowerCase()] ?? {
    dot: '#00e5a0', bg: 'rgba(0,229,160,0.10)', logo: null,
    letter: name.slice(0, 1).toUpperCase(), color: '#00e5a0',
  };
}

function BrokerLogo({ brokerName }) {
  const cfg                 = getBrokerConfig(brokerName);
  const [failed, setFailed] = useState(false);
  return (
    <div className={styles.brokerLogoWrap} style={{ background: cfg.bg }}>
      {cfg.logo && !failed ? (
        <img src={cfg.logo} alt={brokerName} className={styles.brokerLogoImg} onError={() => setFailed(true)} />
      ) : (
        <span className={styles.brokerLogoLetter} style={{ color: cfg.color }}>{cfg.letter}</span>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   PILLS
══════════════════════════════════════════════ */
function StatusPill({ status }) {
  const cls = { open: styles.pillOpen, closed: styles.pillClosed, partial: styles.pillPartial, cancelled: styles.pillCancelled };
  const meta = STATUS_META[status] ?? { label: (status ?? '—').toUpperCase() };
  return <span className={`${styles.pill} ${cls[status] ?? styles.pillClosed}`}>{meta.label}</span>;
}

function SignalPill({ signal }) {
  const s = (signal ?? '').toLowerCase();
  return (
    <span className={`${styles.pill} ${(s === 'short' || s === 'sell') ? styles.pillSell : styles.pillBuy}`}>
      {(signal ?? '—').toUpperCase()}
    </span>
  );
}

/* ══════════════════════════════════════════════
   STAT CARD
══════════════════════════════════════════════ */
function StatCard({ label, value, sub, accent = 'accentGreen', valueClass = 'valNeutral', badge }) {
  return (
    <div className={`${styles.statCard} ${styles[accent]}`}>
      <div className={styles.statLabel}>{label}</div>
      <div className={`${styles.statValue} ${styles[valueClass]}`}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
      {badge && (
        <div className={`${styles.statBadge} ${badge.up ? styles.badgeUp : styles.badgeDown}`}>
          {badge.up ? '▲' : '▼'} {badge.text}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   BROKER CARD
══════════════════════════════════════════════ */
function BrokerCard({ broker }) {
  const p       = broker.performance ?? {};
  const pnl     = p.total_pnl       ?? 0;
  const trades  = p.total_trades     ?? 0;
  const winRate = p.win_rate         ?? 0;
  const won     = p.winning_trades   ?? 0;
  const lost    = p.losing_trades    ?? 0;
  const tokenAt = broker.access_token_updated_at;

  return (
    <div className={styles.brokerCard}>
      <div className={styles.brokerCardHeader}>
        <div className={styles.brokerNameRow}>
          <BrokerLogo brokerName={broker.broker_name} />
          <div>
            <div className={styles.brokerLabel}>{broker.display_name}</div>
            <div className={styles.brokerAccount}>{broker.client_id}</div>
          </div>
        </div>
        <div className={styles.brokerStatus}>
          <span className={styles.brokerStatusDot} />
          ACTIVE
        </div>
      </div>

      <div className={styles.brokerMetrics}>
        <div className={styles.bm}>
          <div className={styles.bmLabel}>P&amp;L</div>
          <div className={`${styles.bmValue} ${pnl >= 0 ? styles.valGreen : styles.valRed}`}>{formatPnL(pnl)}</div>
        </div>
        <div className={styles.bm}>
          <div className={styles.bmLabel}>Trades</div>
          <div className={`${styles.bmValue} ${styles.valNeutral}`}>{trades}</div>
        </div>
        <div className={styles.bm}>
          <div className={styles.bmLabel}>Win Rate</div>
          <div className={`${styles.bmValue} ${winRate >= 50 ? styles.valGreen : styles.valRed}`}>
            {Number(winRate).toFixed(1)}%
          </div>
        </div>
        <div className={styles.bm}>
          <div className={styles.bmLabel}>Won / Lost</div>
          <div className={`${styles.bmValue} ${styles.valNeutral}`}>
            <span style={{ color: '#00e5a0' }}>{won}</span>
            <span style={{ color: '#5a6a82', margin: '0 5px' }}>/</span>
            <span style={{ color: '#ff4d6d' }}>{lost}</span>
          </div>
        </div>
      </div>

      <div className={styles.brokerFooter}>
        <div className={styles.tokenInfo}><span className={styles.tokenDot} />Token refreshed</div>
        <div className={styles.tokenTime}>{tokenAt ?? '—'}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ORDERS MINI TABLE
══════════════════════════════════════════════ */
function OrdersTable({ orders, loading }) {
  const COLS = ['Date', 'Instrument', 'Expiry', 'Signal', 'Broker', 'Strikes', 'Qty', 'PnL', 'Status'];
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead><tr>{COLS.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className={styles.skeletonRow}>
                {COLS.map((_, j) => <td key={j}><span className={styles.skeleton} style={{ width: `${55 + (j * 7) % 30}%` }} /></td>)}
              </tr>
            ))
          ) : orders.length === 0 ? (
            <tr><td colSpan={COLS.length} className={styles.tableEmpty}>No orders found</td></tr>
          ) : (
            orders.map(o => {
              const id         = o._id?.$oid ?? o._id;
              const sellStrike = o.sell_leg?.strike;
              const buyStrike  = o.buy_leg?.strike;
              const strikes    = (sellStrike && buyStrike) ? `${sellStrike} / ${buyStrike}` : sellStrike ?? buyStrike ?? '—';
              const qty        = o.sell_leg?.quantity ?? o.buy_leg?.quantity ?? o.lot_size ?? '—';
              return (
                <tr key={id}>
                  <td className={styles.tdMuted}>{o.created_at?.slice(0, 10) ?? '—'}</td>
                  <td className={styles.tdInstrument}>{o.instrument}</td>
                  <td className={styles.tdMuted}>{o.expiry}</td>
                  <td><SignalPill signal={o.signal} /></td>
                  <td className={styles.tdMuted}>{o.broker_name?.toUpperCase()}</td>
                  <td className={styles.tdMuted}>{strikes}</td>
                  <td className={styles.tdNum}>{qty}</td>
                  <td className={styles.tdNum} style={{ color: getPnLColor(o.total_pnl) }}>
                    {o.total_pnl != null ? formatPnL(o.total_pnl) : '—'}
                  </td>
                  <td><StatusPill status={o.status} /></td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD
   NOTE: Navbar comes from App.js → AppLayout
   This component only renders the page content
══════════════════════════════════════════════ */
export default function Dashboard() {
  const { user } = useAuth();

  const [activeTab,     setActiveTab]     = useState('all');
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orders,        setOrders]        = useState([]);
  const [activeFilter,  setActiveFilter]  = useState('all');

  const meData  = user?.data ?? user ?? {};
  const brokers = meData.brokers ?? [];

  const totalPnl    = brokers.reduce((s, b) => s + (b.performance?.total_pnl      ?? 0), 0);
  const totalTrades = brokers.reduce((s, b) => s + (b.performance?.total_trades   ?? 0), 0);
  const totalWon    = brokers.reduce((s, b) => s + (b.performance?.winning_trades ?? 0), 0);
  const totalLost   = brokers.reduce((s, b) => s + (b.performance?.losing_trades  ?? 0), 0);
  const winRate     = totalTrades > 0 ? (totalWon / totalTrades) * 100 : 0;

  const visibleBrokers = activeTab === 'all'
    ? brokers
    : brokers.filter(b => b.broker_name?.toLowerCase() === activeTab);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res     = await getOrders({ limit: 8 });
      const payload = res.data?.data ?? res.data;
      setOrders(Array.isArray(payload) ? payload : payload?.orders ?? []);
    } catch { setOrders([]); }
    finally  { setOrdersLoading(false); }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter(o => o.status === activeFilter);

  return (
    <main className={styles.main}>

      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Portfolio Overview</h1>
        <div className={styles.pageSub}>
          {new Date().toDateString()} · NSE / BSE ·{' '}
          {activeTab === 'all' ? 'All brokers' : brokers.find(b => b.broker_name === activeTab)?.display_name ?? activeTab}
        </div>
      </div>

      {/* Broker tabs */}
      <div className={styles.brokerTabs}>
        <button
          className={`${styles.brokerTab} ${activeTab === 'all' ? styles.brokerTabActive : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All brokers <span className={styles.tabCount}>{brokers.length}</span>
        </button>
        {brokers.map(b => {
          const tabKey = b.broker_name?.toLowerCase();
          const cfg    = getBrokerConfig(b.broker_name);
          return (
            <button
              key={b.broker_id}
              className={`${styles.brokerTab} ${activeTab === tabKey ? styles.brokerTabActive : ''}`}
              onClick={() => setActiveTab(tabKey)}
            >
              <span className={styles.brokerDot} style={{ background: cfg.dot }} />
              {b.display_name}
              <span className={styles.tabCount}>{b.performance?.total_trades ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Stat cards */}
      <div className={styles.statGrid}>
        <StatCard
          label="Total P&L" value={formatPnL(totalPnl)} sub="All brokers combined"
          accent="accentGreen" valueClass={totalPnl >= 0 ? 'valGreen' : 'valRed'}
          badge={totalPnl >= 0 ? { up: true, text: 'Profitable' } : { up: false, text: 'In loss' }}
        />
        <StatCard label="Total Trades" value={totalTrades} sub={`${totalWon} won · ${totalLost} lost`} accent="accentBlue" valueClass="valNeutral" />
        <StatCard label="Win Rate" value={`${winRate.toFixed(1)}%`} sub="Closed trades only" accent="accentAmber" valueClass={winRate >= 50 ? 'valGreen' : 'valRed'} />
        <StatCard label="Won / Lost" value={`${totalWon} / ${totalLost}`} sub="Trade outcomes" accent="accentRed" valueClass="valNeutral" />
      </div>

      {/* Broker cards */}
      <div className={styles.brokerGrid}>
        {visibleBrokers.length === 0 ? (
          <div style={{ color: 'var(--muted2)', fontSize: 13, padding: '20px 0', gridColumn: '1/-1' }}>No brokers found</div>
        ) : (
          visibleBrokers.map(b => <BrokerCard key={b.broker_id} broker={b} />)
        )}
      </div>

      {/* Recent orders */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Recent Orders</div>
        <Link to="/orders" className={styles.viewAll}>View all →</Link>
      </div>

      <div className={styles.ordersPanel}>
        <div className={styles.ordersFilters}>
          {['all', 'open', 'closed', 'partial', 'cancelled'].map(f => (
            <button
              key={f}
              className={`${styles.filterChip} ${activeFilter === f ? styles.filterChipActive : ''}`}
              onClick={() => setActiveFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <OrdersTable orders={filteredOrders} loading={ordersLoading} />
      </div>

    </main>
  );
}